<template>
  <div>
    <PageHeader
      :page="page"
      has-return
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    >
      <ActionButton
        v-if="!isReadOnly"
        class="m-1"
        text="Guardar"
        i="fas fa-save"
        variant="outline-success m-1"
        @click="doSave()"
      />
      <DropDown
        v-if="requestSaved"
        class="mb-1 ml-1"
        variant="standard btn-change-status"
        title="Manejo de Estatus"
        :items="actionItems"
        icon="fas fa-exchange-alt "
      />
    </PageHeader>
    <StatusChangePanel
      v-if="panelOptions.show"
      :panel-options="panelOptions"
      @continue="onContinueStatusChange"
      @cancel="panelOptions.show = false"
    />
    <StatusChangePanel
      v-for="options in statusChanges"
      :key="options.targetStatus.id"
      :panel-options="options"
    />
    <div class="d-flex">
      <div class="pt-2 pb-4">
        <SectionTitle
          class="responsive-section-title"
          icon="fas fa-user-edit"
          content="Información General de Solicitud"
          bg-icon="orange"
        />
      </div>
    </div>
    <ValidationObserver ref="schoolInformationForm">
      <SchoolInformation
        :key="model.statusId"
        :value="model"
        class="mb-4 px-0"
        :readonly="isSchoolInformationReadOnly"
      />
    </ValidationObserver>
    <ValidationObserver ref="materialsEquipmentForm">
      <MaterialsEquipment
        v-if="requestSaved"
        ref="materialsEquipmentList"
        :materials-equipment="model.materials"
        :saloon="model.classroom"
        class="mb-4 px-0"
        :submitted="isReadOnly"
      />
    </ValidationObserver>
    <ValidationObserver ref="newMaterialsEquipmentForm">
      <NewMaterialsEquipment
        v-if="requestSaved"
        ref="newMaterialsEquipmentList"
        :materials-equipment="model.newMaterials"
        :saloon="model.classroom"
        class="mb-4 px-0"
        :submitted="isReadOnly"
        :hide-btn="isReadOnly && model.newMaterials.length == 0"
      />
    </ValidationObserver>
    <ValidationObserver ref="justificationForm">
      <Justification
        v-if="requestSaved"
        :value="model"
        class="mb-4 px-0"
        :readonly="isReadOnly"
      />
    </ValidationObserver>
    <ValidationObserver ref="documentForm">
      <Document
        v-if="requestSaved"
        :id="model.id"
        ref="materialsDocuments"
        :documents="model.documents"
        class="mb-4 px-0"
        :read-only="isReadOnly"
        @update-documents="refreshDocuments()"
      />
    </ValidationObserver>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import DropDown from 'dropdown';
import alert from '@/mixin/alert.mixin';
import {
  submitRequest,
  changeRequestStatus,
  getRequestDetail,
  getRequestDocuments,
  getTargetRequestStatuses,
} from '@/modules/materials-equipment/api.js';
import { materialsEquipmentRequestStatus } from '@/utils/constants/pei/materials-equipment/MaterialsEquipmentRequestStatus';
import levelTypes from '@/utils/constants/pei/materials-equipment/levelTypes.js';
import SchoolInformation from './components/SchoolInformation';
import MaterialsEquipment from './components/MaterialsEquipment/Index';
import NewMaterialsEquipment from './components/NewMaterialsEquipment/Index';
import Justification from './components/Justification';
import Document from './components/Document';
import StatusChangePanel from './components/StatusChangePanel';
import codeService from '@/modules/common/code.api';

const docTypes = {
  COTIZACION: 228,
  QUERELLA: 227,
  OTRO: 229,
};

const defaultPanelOptions = () => ({
  show: false,
  title: null,
  user: null,
  defaultComment: null,
  readOnly: false,
  targetStatus: null,
  color: null,
});

export default {
  name: 'AddMaterialsEquipmentRequest',
  components: {
    PageHeader,
    SectionTitle,
    ActionButton,
    DropDown,
    SchoolInformation,
    MaterialsEquipment,
    Justification,
    Document,
    StatusChangePanel,
    NewMaterialsEquipment,
  },
  mixins: [alert],
  props: {
    requestId: { type: [Number, String], default: null },
  },
  data: () => ({
    docTypes,
    page: {
      icon: 'fas fa-user-edit',
      title: 'Manejo de Solicitud de Materiales y Equipos',
      routes: ['Inicio', 'PEI', 'Manejo de Solicitud de Materiales y Equipos'],
    },
    model: {
      id: 0,
      levelTypeId: null,
      academicYearId: null,
      hasPreviouslySubmitted: null,
      schoolBelongsTo: null,
      school: null,
      center: null,
      locationSchoolId: null,
      custodian: null,
      region: null,
      regionId: null,
      municipalityId: null,
      municipality: null,
      classroom: null,
      materials: [],
      justification: null,
      isComplaintRequest: null,
      isNewCreationRequest: null,
      studentEnrollment: null,
      itemList: [],
      documents: [],
      newMaterials: [],
      statusId: 0,
      status: {},
      statusChanges: [],
    },
    panelOptions: defaultPanelOptions(),
    targetStatuses: [],
    showMaterialsQtyMsg: false,
    showRequiredMaterialsMsg: false,
    showDocumentMsg: false,
    categories: [],
  }),
  computed: {
    requestSaved() {
      return this.model.id;
    },
    isNew() {
      return (
        !this.requestSaved ||
        this.model.statusId === materialsEquipmentRequestStatus.Nuevo
      );
    },
    isSubmitted() {
      return this.model.statusId === materialsEquipmentRequestStatus.Sometido;
    },
    isApproved() {
      return this.model.statusId === materialsEquipmentRequestStatus.Aprobada;
    },
    isReadOnly() {
      return this.isSubmitted || this.isApproved;
    },
    isSchoolInformationReadOnly() {
      return (
        this.isSubmitted || this.isApproved || this.isPendingClassification
      );
    },
    isReturnedSchool() {
      return (
        this.model.statusId === materialsEquipmentRequestStatus.DevueltoEscuela
      );
    },
    isPendingClassification() {
      return (
        this.model.statusId ===
        materialsEquipmentRequestStatus.PendienteClasificacion
      );
    },
    actionItems() {
      return this.targetStatuses.map((status) => {
        const action = {
          label: status.description,
          click: () => this.setCurrentStatusTarget(status),
        };
        if (
          status.id == materialsEquipmentRequestStatus.Sometido &&
          this.targetStatuses.length == 1
        )
          return {
            ...action,
            icon: 'fas fa-flag',
            class: 'ab-btn ab-btn-success pt-2',
          };
        return action;
      });
    },
    statusChanges() {
      return this.model.statusChanges
        ? this.model.statusChanges
            .filter((statusChange) => this.showStatus(statusChange))
            .map((statusChange) => ({
              ...this.getStatusOptions(statusChange.statusId),
              user: statusChange.user,
              userRole: statusChange.userRole,
              defaultComment: statusChange.comment,
              targetStatus: { id: statusChange.statusId },
              readonly: true,
            }))
        : [];
    },
  },
  methods: {
    showStatus(statusChange) {
      if (statusChange.comment) {
        if (
          !this.isReturnedSchool &&
          statusChange.statusId ===
            materialsEquipmentRequestStatus.DevueltoEscuela
        ) {
          return false;
        }

        return true;
      }

      return false;
    },
    async doSave() {
      const isSchoolValid = await this.$refs.schoolInformationForm.validate();

      if (!isSchoolValid) {
        return this.ShowToast(
          'Alerta',
          'Favor de completar la información solicitada para crear la solicitud.',
          'error'
        );
      }

      let payload = {
        id: this.model.id,
        levelTypeId: parseInt(this.model.levelTypeId),
        academicYearId: this.model.academicYearId,
        classroomTypeId: this.model.classroom.id,
        justification: this.model.justification,
        detail: this.model.materials.map((item) => ({
          itemTypeId: item.article?.id,
          quantity: item.quantity,
          categoryId: item.category?.id,
        })),
        newDetail: this.model.newMaterials.map((item) => ({
          description: item.article.name,
          categoryId: item.category?.id,
          quantity: item.quantity,
        })),
      };

      if (this.model.levelTypeId == levelTypes.Center) {
        payload = { ...payload, centerId: this.model.center.id };
      } else if (this.model.levelTypeId == levelTypes.School) {
        payload = {
          ...payload,
          regionId: this.model.region.id,
          municipalityId: this.model.municipality.id,
          locationSchoolId: this.model.school.id,
          custodianId:
            this.model.custodian.id == 1 ? null : this.model.custodian.id,
          custodianName: this.model.custodianName,
          studentEnrollment: this.model.studentEnrollment,
          schoolBelongsTo: this.model.schoolBelongsTo,
          hasPreviouslySubmitted: this.model.hasPreviouslySubmitted,
          isComplaintRequest: this.model.isComplaintRequest,
          isNewCreationRequest: this.model.isNewCreationRequest,
        };
      }

      await submitRequest(payload)
        .then(async (res) => {
          const isFirstSaved = !this.model.id;
          this.model.id = res.data.id;
          this.model.status = res.data.status;
          if (isFirstSaved) await this.getTargetStatuses();
          this.ShowToast(
            'Confirmación',
            'La información ha sido guardada exitosamente',
            'success'
          );
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    async doSubmit() {
      const isSchoolValid = await this.$refs.schoolInformationForm.validate();
      const isJustiValid = await this.$refs.justificationForm.validate();

      if (
        this.model.materials.length == 0 &&
        this.model.newMaterials.length == 0
      ) {
        return this.ShowToast(
          'Alerta',
          'Debe solicitar al menos un material y/o equipo existente o nuevo.',
          'error'
        );
      }

      this.showMaterialsQtyMsg =
        !(await this.$refs.materialsEquipmentList?.showQtyErrorMsg()) ||
        !(await this.$refs.newMaterialsEquipmentList?.showQtyErrorMsg());

      const match = this.model.documents.find((el) => {
        return el.documentTypeId == this.docTypes.QUERELLA;
      });

      if (this.model.isComplaintRequest && match == undefined) {
        this.$refs.materialsDocuments.showRequiredMsg();
        this.showDocumentMsg = true;
      }

      if (
        !isSchoolValid ||
        !isJustiValid ||
        this.showMaterialsQtyMsg ||
        this.showRequiredMaterialsMsg ||
        this.showDocumentMsg
      ) {
        this.showRequiredMaterialsMsg = false;
        this.showMaterialsQtyMsg = false;
        this.showDocumentMsg = false;
        return this.ShowToast(
          'Alerta',
          'Favor de completar la información solicitada para crear la solicitud.',
          'error'
        );
      }

      await this.doSave();
      await changeRequestStatus(this.model.id, {
        statusId: materialsEquipmentRequestStatus.Sometido,
      })
        .then(async (res) => {
          if (res.status == 200) {
            await this.fetchRequestDetail(this.model.id);

            this.ShowToast(
              'Confirmación',
              'La información ha sido guardada exitosamente',
              'success'
            );
          }
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    async fetchRequestDetail(id) {
      await getRequestDetail(id).then(({ data }) => this.setModel(data));
      await getRequestDocuments(id).then((res) => {
        this.model.documents = res.data;
      });
      await this.getTargetStatuses();
    },
    async fetchCategories() {
      const { data: categories } = await codeService.getMaterialCategoryTypes();
      this.categories = categories;
    },
    async getTargetStatuses() {
      let { data: targetStatuses } = await getTargetRequestStatuses(
        this.model.status.id
      );

      if (
        this.model.status.id == materialsEquipmentRequestStatus.Sometido &&
        this.model.newMaterials.length == 0
      ) {
        targetStatuses = targetStatuses.filter(
          (e) => e.id !== materialsEquipmentRequestStatus.PendienteClasificacion
        );
      }

      if (
        this.model.status.id == materialsEquipmentRequestStatus.Sometido &&
        this.model.newMaterials.length > 0
      ) {
        targetStatuses = targetStatuses.filter(
          (x) => x.id != materialsEquipmentRequestStatus.Aprobada
        );
      }

      this.targetStatuses = targetStatuses;
    },
    async setCurrentStatusTarget(status) {
      if (status.id === materialsEquipmentRequestStatus.Sometido)
        await this.doSubmit();
      else
        this.panelOptions = {
          ...this.panelOptions,
          ...this.getStatusOptions(status.id),
        };

      this.panelOptions.targetStatus = status;
      this.panelOptions.show = status.isCommentNeeded;
    },
    async onContinueStatusChange(comment) {
      const response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea cambiar  el estatus de ${this.model.status.name} a ${this.panelOptions.targetStatus.name}?`
      );
      if (!response) return;
      const statusId = this.panelOptions.targetStatus.id;
      changeRequestStatus(this.model.id, { statusId, comment })
        .then(async (res) => {
          if (res.status == 200) {
            await this.fetchRequestDetail(this.model.id);
            this.panelOptions = defaultPanelOptions();
            this.ShowToast(
              'Proceso exitoso.',
              'La solicitud ha sido cambiada de estatus exitosamente.',
              'success'
            );
          }
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    setModel(data) {
      this.model = { ...this.model, ...data };
      this.model.materials = data.detail.filter((x) => x.isAdditional == false);
      this.model.newMaterials = data.detail.filter(
        (x) => x.isAdditional == true
      );
    },
    getStatusOptions(statusId) {
      const panelOptions = {};
      switch (statusId) {
        case materialsEquipmentRequestStatus.DevueltoEscuela:
          panelOptions.title = 'Devolución a la Escuela';
          panelOptions.color = 'danger';
          break;
        case materialsEquipmentRequestStatus.Aprobada:
          panelOptions.title = 'Solicitud Aprobada';
          panelOptions.color = 'success';
          break;
        case materialsEquipmentRequestStatus.PendienteClasificacion:
          panelOptions.title = 'Solicitud Pendiente de Clasificación';
          panelOptions.color = 'success';
          break;
      }
      return panelOptions;
    },
    async refreshDocuments() {
      await getRequestDocuments(this.model.id).then((res) => {
        this.model.documents = res.data;
      });
    },
  },
  async mounted() {
    if (this.requestId != null && this.requestId > 0) {
      await this.fetchCategories();
      await this.fetchRequestDetail(this.requestId);
    }
  },
};
</script>
<style lang="scss" scoped>
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';
::v-deep .ab-btn-success {
  color: map.get($colors, 'white') !important;
  background-color: map.get($colors, 'green') !important;
  border-color: map.get($colors, 'green') !important;
}
</style>
